import { AddressingOptions } from '@funnellab/shared/src/types/directus-enums';
import {
  type ChatbotQuestionForCreate,
  type ChatbotAnswer,
  type ChatbotConfiguration,
  type ChatbotConfigurationForUpdate,
  type ChatbotQuestion,
  type ChatbotQuestionForUpdate,
} from '@src/types/chatbot';
import {
  type ChatbotQuestionDTO,
  type ChatbotQuestionDTOForUpdate,
} from '@funnellab/shared/src/dtos/chatbot/ChatbotQuestionDTO';
import { directusClient } from '@src/api/DirectusClient';
import type {
  ChatbotConfigurationDTO,
  ChatbotConfigurationDTOForUpdate,
} from '@funnellab/shared/src/dtos/chatbot/ChatbotConfigurationDTO';
import { type ChatbotAnswerDTOForUpdate } from '@funnellab/shared/src/dtos/chatbot/ChatbotAnswerDTO';

type Relation =
  | 'questions'
  | 'addressing'
  | 'chatbotBuilderFeature'
  | 'employerBrand'
  | 'displayed';
type ConfigContext = 'Chatbot' | 'Preview' | 'Edit' | 'Plain';
const defaultConfigRelations: Relation[] = [
  'questions',
  'addressing',
  'employerBrand',
];
const relationContextMapping: Record<ConfigContext, Relation[]> = {
  Chatbot: defaultConfigRelations,
  Preview: [...defaultConfigRelations, 'chatbotBuilderFeature'],
  Edit: [...defaultConfigRelations, 'displayed', 'chatbotBuilderFeature'],
  Plain: [],
};

const getConfigurationInternal = async (
  jobId: string,
  context: ConfigContext = 'Preview',
  draft: boolean = false,
): Promise<ChatbotConfiguration> => {
  const configuration = await directusClient.getChatbotConfigurationForJob(
    jobId,
    relationContextMapping[context],
    draft,
  );
  return mapDtoToConfiguration(configuration);
};

export const getConfigurationForEdit = async (
  jobId: string,
): Promise<ChatbotConfiguration> => {
  try {
    return await getConfigurationInternal(jobId, 'Edit', true);
  } catch {
    console.info(
      `No draft configuration found for job ${jobId}, fetching live configuration instead.`,
    );
    return await getConfigurationInternal(jobId, 'Edit', false);
  }
};

export const getDraftConfiguration = async (
  jobId: string,
): Promise<ChatbotConfiguration> => {
  return getConfigurationInternal(jobId, 'Plain', true);
};

export const getLiveConfiguration = async (
  jobId: string,
  context: ConfigContext = 'Preview',
): Promise<ChatbotConfiguration> => {
  return getConfigurationInternal(jobId, context, false);
};

export const updateConfiguration = async (
  jobId: string,
  config: ChatbotConfigurationForUpdate,
): Promise<ChatbotConfiguration> => {
  const updatedConfig = await directusClient.updateChatbotConfigurationForJob(
    jobId,
    mapConfigToDTOForUpdate(config),
    relationContextMapping.Edit,
  );
  return mapDtoToConfiguration(updatedConfig);
};

export const publishChatbotConfigurationForJob = async (
  jobId: string,
  context: ConfigContext = 'Edit',
): Promise<ChatbotConfiguration> => {
  const publishedConfig =
    await directusClient.publishChatbotConfigurationForJob(
      jobId,
      relationContextMapping[context],
    );
  return mapDtoToConfiguration(publishedConfig);
};

const mapConfigToDTOForUpdate = (
  config: ChatbotConfigurationForUpdate,
): ChatbotConfigurationDTOForUpdate => {
  const configDTO: ChatbotConfigurationDTOForUpdate = {
    companyDescription: config.companyDescription,
    tasks: config.tasks,
    qualifications: config.qualifications,
    benefits: config.benefits,
    website: config.website,
  };
  if (config.questions) {
    return {
      ...configDTO,
      questions: config.questions.map(mapQuestionToDTOForUpdate),
    };
  }
  return configDTO;
};

const mapDtoToConfiguration = (
  config: ChatbotConfigurationDTO,
): ChatbotConfiguration => {
  const configuration: ChatbotConfiguration = {
    id: config.id,
    companyDescription: config.companyDescription,
    tasks: config.tasks,
    qualifications: config.qualifications,
    benefits: config.benefits,
    addressing: config.addressing ?? AddressingOptions.GERMAN_DU,
    useChatbotBuilder: !!config.hasChatbotBuilder,
    employerBrand: config.employerBrand,
    website: config.website ?? null,
    isDraft: !!config.isDraft,
  };
  if (config.questions) {
    configuration.questions = mapDtosToQuestions(config.questions);
  }
  return configuration;
};

const mapDtosToQuestions = (
  questionDtos: ChatbotQuestionDTO[],
): ChatbotQuestion[] => {
  return questionDtos;
};

const mapQuestionToDTOForUpdate = (
  question: Partial<ChatbotQuestionForCreate | ChatbotQuestionForUpdate>,
): ChatbotQuestionDTOForUpdate => {
  return {
    ...question,
    answers: question.answers?.length
      ? question.answers.map(mapAnswerToDTOForUpdate)
      : undefined,
  };
};

const mapAnswerToDTOForUpdate = (
  answer: Partial<ChatbotAnswer>,
): ChatbotAnswerDTOForUpdate => {
  return {
    ...answer,
    rating: answer.rating ?? undefined,
  };
};
